import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './InsightPanel.css';

/**
 * InsightPanel — panneau d'insights post-génération de graphe.
 *
 * Apparaît automatiquement une fois que le graphe contient ≥ 5 nœuds.
 * Affiche un résumé LLM, des cards d'insights priorisés et des actions suggérées.
 *
 * Props:
 *  - insights   {Object}   Retour de GET /api/insights
 *  - onDismiss  {Function} Ferme le panneau
 *  - onRefresh  {Function} Force le rechargement des insights
 *  - onSectionClick {Function} Reçoit un sectionId → ouvre la section Lab
 *  - loading    {boolean}  Affiche le skeleton pendant le fetch
 */
export default function InsightPanel({ insights, onDismiss, onRefresh, onSectionClick, onSectionNav, onNodeSelect, onActionClick, loading }) {
  const { t } = useTranslation();
  const [refreshing, setRefreshing] = useState(false);
  const [expandedInsight, setExpandedInsight] = useState(null);

  const handleRefresh = async () => {
    if (!onRefresh || refreshing) return;
    setRefreshing(true);
    await onRefresh();
    setRefreshing(false);
  };

  const priorityConfig = {
    high:   { label: t('insightPanel.priority.high',   'Critique'), className: 'priority-high' },
    medium: { label: t('insightPanel.priority.medium', 'Modéré'),   className: 'priority-medium' },
    low:    { label: t('insightPanel.priority.low',    'Faible'),   className: 'priority-low' }
  };

  const sectionLabels = {
    'tests':          t('insightPanel.sections.tests',    'Tests'),
    'analysis':       t('insightPanel.sections.analysis',  'Analyse'),
    'specifications': t('insightPanel.sections.specs',     'Spécifications'),
    'relations':      t('insightPanel.sections.relations', 'Relations'),
    'vectorSearch':   t('insightPanel.sections.vectorSearch', 'Recherche'),
    'topology':       t('insightPanel.sections.topology', 'Topologie'),
  };

  const allowedSections = new Set(['tests', 'analysis', 'specifications', 'relations', 'vectorSearch', 'createNode', 'nodesList', 'topology']);

  const normalizeSectionId = (sectionId) => {
    if (!sectionId) return null;

    const raw = sectionId.toString();
    const lower = raw.toLowerCase();

    const mapped =
      (allowedSections.has(raw) ? raw : null)
      || (lower === 'topology' ? 'topology' : null)
      || (lower.includes('hub') || lower.includes('central') || lower.includes('isolated') ? 'topology' : null)
      || (lower.includes('test') ? 'tests' : null)
      || (lower.includes('spec') ? 'specifications' : null)
      || ((lower.includes('relation') || lower.includes('graph')) ? 'relations' : null)
      || ((lower.includes('vector') || lower.includes('search')) ? 'vectorSearch' : null)
      || ((lower.includes('impact') || lower.includes('analysis') || lower.includes('quality') || lower.includes('risk')) ? 'topology' : null);

    return mapped || null;
  };

  // ── Skeleton de chargement ──────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="insight-panel insight-panel--loading" role="status" aria-busy="true">
        <div className="insight-panel__header">
          <span className="insight-panel__icon">🧠</span>
          <span className="insight-panel__title">{t('insightPanel.loading', 'Analyse en cours...')}</span>
        </div>
        <div className="insight-panel__skeleton">
          <div className="skeleton-line skeleton-line--long" />
          <div className="skeleton-line skeleton-line--medium" />
          <div className="skeleton-cards">
            <div className="skeleton-card" />
            <div className="skeleton-card" />
            <div className="skeleton-card" />
          </div>
        </div>
      </div>
    );
  }

  // ── État idle : pas encore analysé ─────────────────────────────────────────
  if (!insights || !insights.ready) {
    return (
      <div className="insight-panel insight-panel--idle" role="complementary">
        <div className="insight-panel__header">
          <div className="insight-panel__header-left">
            <span className="insight-panel__icon">🧠</span>
            <div>
              <h3 className="insight-panel__title">{t('insightPanel.title', 'Insight Engine')}</h3>
              <span className="insight-panel__meta">{t('insightPanel.idleSubtitle', 'Analysez la structure de votre graphe')}</span>
            </div>
          </div>
          <button
            className="insight-panel__btn-dismiss"
            onClick={onDismiss}
            title={t('insightPanel.dismiss', 'Fermer')}
            aria-label={t('insightPanel.dismiss', 'Fermer')}
          >✕</button>
        </div>
        <div className="insight-panel__idle-body">
          <p className="insight-panel__idle-desc">
            {t('insightPanel.idleDesc', "Lancez une analyse pour détecter les patterns, les nœuds isolés et obtenir des recommandations d'amélioration.")}
          </p>
          <button
            className="insight-panel__btn-analyze"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            {refreshing ? '⏳ ' : '🔍 '}
            {t('insightPanel.analyze', 'Analyser le graphe →')}
          </button>
        </div>
      </div>
    );
  }

  const { summary, insights: insightCards = [], topology, suggestedActions = [], generatedAt, cached, stale, staleDiff } = insights;

  return (
    <div className="insight-panel" role="complementary" aria-label={t('insightPanel.ariaLabel', 'Insights du graphe')}>
      {/* ── Bannière "analyse périmée" ───────────────────────────────────── */}
      {stale && (
        <div className="insight-panel__stale-banner">
          <span className="stale-icon">⚠️</span>
          <span className="stale-text">
            {staleDiff > 0
              ? t('insightPanel.staleNodes', `${staleDiff} nœud(s) ajouté(s) depuis cette analyse`, { count: staleDiff })
              : t('insightPanel.staleChanged', 'Des changements ont eu lieu depuis cette analyse')
            }
          </span>
          <button
            className="stale-refresh-btn"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            {refreshing ? '⏳' : '🔄'} {t('insightPanel.staleRefresh', 'Actualiser →')}
          </button>
        </div>
      )}

      {/* ── Header ────────────────────────────────────────────────────────── */}
      <div className="insight-panel__header">
        <div className="insight-panel__header-left">
          <span className="insight-panel__icon">🧠</span>
          <div>
            <h3 className="insight-panel__title">
              {t('insightPanel.title', 'Insight Engine')}
            </h3>
            {generatedAt && (
              <span className="insight-panel__meta">
                {cached && `${t('insightPanel.cached', 'Cache')} · `}
                {new Date(generatedAt).toLocaleTimeString()}
              </span>
            )}
          </div>
        </div>
        <div className="insight-panel__header-actions">
          <button
            className="insight-panel__btn-refresh"
            onClick={handleRefresh}
            disabled={refreshing}
            title={t('insightPanel.refresh', 'Rafraîchir les insights')}
          >
            {refreshing ? '⏳' : '🔄'}
          </button>
          <button
            className="insight-panel__btn-dismiss"
            onClick={onDismiss}
            title={t('insightPanel.dismiss', 'Fermer')}
            aria-label={t('insightPanel.dismiss', 'Fermer')}
          >
            ✕
          </button>
        </div>
      </div>

      {/* ── Statistiques topologiques ────────────────────────────────────── */}
      {topology && (
        <div className="insight-panel__topology-bar">
          <span className="topo-stat">
            <strong>{topology.nodeCount}</strong> {t('insightPanel.topo.nodes', 'nœuds')}
          </span>
          <span className="topo-divider">·</span>
          <span className="topo-stat">
            <strong>{topology.relationCount}</strong> {t('insightPanel.topo.relations', 'relations')}
          </span>
          {topology.hubNodes?.length > 0 && (
            <>
              <span className="topo-divider">·</span>
              <span className="topo-stat topo-stat--warn">
                <strong>{topology.hubNodes.length}</strong> {t('insightPanel.topo.hubs', 'hub(s)')}
              </span>
            </>
          )}
          {topology.isolatedNodes?.length > 0 && (
            <>
              <span className="topo-divider">·</span>
              <span className="topo-stat topo-stat--muted">
                <strong>{topology.isolatedNodes.length}</strong> {t('insightPanel.topo.isolated', 'isolé(s)')}
              </span>
            </>
          )}
        </div>
      )}

      {/* ── Résumé LLM ────────────────────────────────────────────────────── */}
      {summary && (
        <p className="insight-panel__summary">{summary}</p>
      )}

      {/* ── Insight cards ─────────────────────────────────────────────────── */}
      {insightCards.length > 0 && (
        <div className="insight-panel__cards">
          {insightCards.map((insight) => {
            const pConf = priorityConfig[insight.priority] || priorityConfig.medium;

            // Nœuds topology associés à cet insight (fallback IDs connus)
            const hubNodes      = (topology?.hubNodes      || []).map(n => ({ name: n.name, type: n.type, meta: `${n.degree} connexions` }));
            const untestedNodes = (topology?.untested      || []).map(n => ({ name: n.name, type: n.type, meta: `${n.inDegree} déps` }));
            const isolatedNodes = (topology?.isolatedNodes || []).map(n => ({ name: n.name, type: n.type, meta: 'isolé' }));

            // Matcher par id ET titre/description pour couvrir les IDs LLM variables
            const id    = (insight.id    || '').toLowerCase();
            const title = (insight.title || '').toLowerCase();
            const desc  = (insight.description || '').toLowerCase();
            const text  = `${id} ${title} ${desc}`;

            const isHubInsight = text.includes('hub') || text.includes('connecté') || text.includes('central') || text.includes('dépendances');
            const isTestInsight = text.includes('test') || text.includes('couverture') || text.includes('critique') || text.includes('critical');
            const isIsolatedInsight = text.includes('isolé') || text.includes('isolated');
            const isRiskInsight = text.includes('risk') || text.includes('zone');

            let topoNodes = null;
            let derivedSection = normalizeSectionId(insight.section); // section dérivée du contenu si manquante

            if (isHubInsight) {
              topoNodes = hubNodes;
              derivedSection = derivedSection || 'topology';
            } else if (isTestInsight) {
              topoNodes = untestedNodes;
              derivedSection = derivedSection || 'topology';
            } else if (isIsolatedInsight) {
              topoNodes = isolatedNodes;
              derivedSection = derivedSection || 'topology';
            } else if (isRiskInsight) {
              topoNodes = isolatedNodes;
              derivedSection = derivedSection || 'topology';
            }
            // Si le tableau résultant est vide, on ne l'utilise pas (évite le bouton "0 nœuds")
            if (topoNodes && topoNodes.length === 0) topoNodes = null;

            const isExpanded = expandedInsight === insight.id;

            return (
              <div
                key={insight.id}
                className={`insight-card insight-card--${insight.priority}`}
              >
                <div className="insight-card__top">
                  <span className={`insight-card__badge ${pConf.className}`}>
                    {pConf.label}
                  </span>
                  {topoNodes && topoNodes.length > 0 ? (
                    // Topology insight → toggle node list, ne ferme pas le panel
                    <button
                      className={`insight-card__section-btn ${isExpanded ? 'insight-card__section-btn--active' : ''}`}
                      onClick={() => setExpandedInsight(isExpanded ? null : insight.id)}
                      title={t('insightPanel.showNodes', 'Voir les nœuds concernés')}
                    >
                      {topoNodes.length} nœud{topoNodes.length > 1 ? 's' : ''} {isExpanded ? '▲' : '▼'}
                    </button>
                  ) : (
                    insight.section && onSectionClick && (
                      <button
                        className="insight-card__section-btn"
                        onClick={() => onSectionClick(insight.section)}
                        title={`${t('insightPanel.goTo', 'Voir')} ${sectionLabels[insight.section] || insight.section}`}
                      >
                        {sectionLabels[insight.section] || insight.section} →
                      </button>
                    )
                  )}
                </div>
                <h4 className="insight-card__title">{insight.title}</h4>
                <p className="insight-card__desc">{insight.description}</p>

                {/* Liste de nœuds inline (topology insights) */}
                {isExpanded && topoNodes && (
                  <div className="insight-card__node-list">
                    {topoNodes.map(n => (
                      <div key={n.name} className="insight-node-item">
                        <span className="insight-node-name">{n.name}</span>
                        <span className="insight-node-type">{n.type}</span>
                        <span className="insight-node-meta">{n.meta}</span>
                        {onNodeSelect && (
                          <button
                            className="insight-node-select-btn"
                            onClick={() => onNodeSelect(n.name)}
                            title={`Sélectionner ${n.name}`}
                          >→</button>
                        )}
                      </div>
                    ))}
                    {derivedSection && (onSectionNav || onSectionClick) && (
                      <button
                        className="insight-node-section-link"
                        onClick={() => (onSectionNav || onSectionClick)(derivedSection)}
                      >
                        Ouvrir {sectionLabels[derivedSection] || derivedSection} →
                      </button>
                    )}
                  </div>
                )}

                {insight.priority === 'high' && insight.section && onSectionClick && !topoNodes?.length && (
                  <button
                    className="insight-card__fix-btn"
                    onClick={() => onSectionClick(insight.section)}
                  >
                    🔧 {t('insightPanel.fixNow', 'Corriger maintenant')} →
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Actions suggérées ─────────────────────────────────────────────── */}
      {suggestedActions.length > 0 && (
        <div className="insight-panel__actions">
          <span className="insight-panel__actions-label">
            {t('insightPanel.suggestedActions', 'Actions suggérées')}
          </span>
          <div className="insight-panel__actions-list">
            {suggestedActions.map((action, idx) => (
              <button
                key={idx}
                className={`insight-action-btn insight-action-btn--${action.priority || 'medium'}`}
                title={action.description}
                onClick={() => onActionClick && onActionClick(action)}
              >
                {action.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
